import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlProcedureDiagnosticReportAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';

interface IInnerOsasoV1ProcedureDiagnosticReportAddEditState {
    buttonDisabled?: boolean;
    isProcessing?: boolean;
    proceedWithoutFiles?: boolean;
    showProceedWithoutFiles?: boolean;
}

export function OsasoV1ProcedureDiagnosticReportAddEdit(props: ICustomControlProcedureDiagnosticReportAddEditProps) {
    const episodeContext = useEpisodeContext();

    return (
        <InnerOsasoV1ProcedureDiagnosticReportAddEdit
            {...props}
            {...episodeContext}
        />
    );
}

class InnerOsasoV1ProcedureDiagnosticReportAddEdit extends Component<
    ICustomControlProcedureDiagnosticReportAddEditProps & IEpisodeContext,
    IInnerOsasoV1ProcedureDiagnosticReportAddEditState
> {
    public render() {
        return (
            <ProcessContainer>
                <ProcessItemHeader
                    isFirstItem
                    isItemOpen
                    organizationName={
                        this.props.patient.authorization?.canSeePatientDetails
                            ? this.props.currentProcedure?.performerOrganization?.name
                            : undefined
                    }
                    practitionerName={
                        this.props.patient &&
                        this.props.patient.authorization &&
                        this.props.patient.authorization.canSeePatientDetails &&
                        this.props.currentProcedure &&
                        this.props.currentProcedure.performerPractitioner
                            ? getDisplayName(this.props.currentProcedure.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    serviceType={ServiceType.examination}
                    title={getServiceLabel(this.props.service.code, 'procedure', this.props.service.name)}
                />
                {this.addEditForm()}
            </ProcessContainer>
        );
    }

    public addEditForm() {
        return (
            <>
                <Form
                    onSubmit={async () => null}
                    showOverlay={this.state.isProcessing}
                >
                    <AssetDropzoneWithActions
                        assets={this.props.procedure.assets}
                        assetsBaseUrl={FilesUrlBase.procedures}
                        onChange={async (assets) => await this.props.handleChange('assets', assets)}
                        onUploading={async (isUploading) => await this.setState({ buttonDisabled: isUploading })}
                        processActivityId={this.props.procedure.id}
                        service={this.props.service}
                    />

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.procedure}
                                isExisting={!!this.props.request.id}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveProcedure('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveProcedure('final');
                                }}
                            />,
                        ]}
                    />

                    {this.state.showProceedWithoutFiles && (
                        <ProceedWithoutFiles
                            entity={ProcessEntity.procedure}
                            onNo={async () =>
                                await this.setState({ showProceedWithoutFiles: false, buttonDisabled: false })
                            }
                            onYes={async () => {
                                await this.setState({ showProceedWithoutFiles: false, proceedWithoutFiles: true });
                                await this.saveProcedure('final');
                            }}
                            serviceType={this.props.service.serviceType}
                        />
                    )}
                </Form>
            </>
        );
    }

    private async saveProcedure(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedProcedure = await this.props.validateProcedure(saveType);
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (!this.state.proceedWithoutFiles && saveType === 'final' && this.props.procedure.assets.length === 0) {
            return await this.setState({ showProceedWithoutFiles: true });
        }

        const requestId = await this.props.onSaveProcedure(saveType);
        await this.setState({ buttonDisabled: false });
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.props.onSave(requestId);
    }
}

export function osasoV1ProcedureInitialize() {
    return {};
}
